import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import DocumentViewModal from '../components/DocumentViewModal';
import StreamingOutput from '../components/StreamingOutput';
import TemplateComparison from '../components/TemplateComparison';

const STREAMING_ENABLED = process.env.REACT_APP_STREAMING_ENABLED !== 'false';
const MAX_ADDITIONAL_INSTRUCTIONS_LEN = 2000;

const ProjectPage = () => {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [viewingDoc, setViewingDoc] = useState(null);
  const [streamingEndpoint, setStreamingEndpoint] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamedVersionId, setStreamedVersionId] = useState(null);
  const [additionalInstructions, setAdditionalInstructions] = useState('');
  const [instructionSuggestions, setInstructionSuggestions] = useState([]);
  const [showInstructionSuggestions, setShowInstructionSuggestions] = useState(false);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const navigate = useNavigate();

  const fetchProjectData = useCallback(async () => {
    setLoading(true);
    try {
      const [projRes, docsRes, versionRes] = await Promise.all([
        api.get(`/api/projects/${projectId}`),
        api.get(`/api/projects/${projectId}/documents`),
        api.get(`/api/versions/project/${projectId}`),
      ]);
      setProject(projRes.data);
      setDocuments(docsRes.data || []);
      setVersions(versionRes.data || []);
    } catch {
      toast.error('Failed to load project details');
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [projectId, navigate]);

  useEffect(() => {
    fetchProjectData();
  }, [fetchProjectData]);

  const fetchInstructionSuggestions = useCallback(async (query = '') => {
    setSuggestionsLoading(true);
    try {
      const { data } = await api.get(`/api/projects/${projectId}/merge-instructions/suggestions`, {
        params: { q: query || undefined, limit: 8 },
      });
      setInstructionSuggestions(Array.isArray(data?.suggestions) ? data.suggestions : []);
    } catch {
      setInstructionSuggestions([]);
    } finally {
      setSuggestionsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (!showInstructionSuggestions) return undefined;
    const timer = setTimeout(() => {
      fetchInstructionSuggestions(additionalInstructions.trim());
    }, 150);
    return () => clearTimeout(timer);
  }, [additionalInstructions, showInstructionSuggestions, fetchInstructionSuggestions]);

  const templates = useMemo(
    () => documents.filter((doc) => doc.document_type === 'template'),
    [documents]
  );
  const practiceA = useMemo(
    () => documents.find((doc) => doc.document_type === 'practice_a'),
    [documents]
  );
  const practiceB = useMemo(
    () => documents.find((doc) => doc.document_type === 'practice_b'),
    [documents]
  );
  const latestVersion = versions.length > 0 ? versions[0] : null;
  const latestMergeReady = Boolean(latestVersion && latestVersion.has_merge_report && latestVersion.merge_report_status === 'ready');

  const handleFileUpload = async (e, documentType) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', documentType);
    formData.append('upload_method', 'file_upload');

    setUploading(true);
    try {
      const { data } = await api.post(`/api/projects/${projectId}/documents`, formData);
      setDocuments((prev) => [...prev, data]);
      toast.success(`${documentType.replace('_', ' ')} uploaded`);
    } catch {
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleMerge = async () => {
    const template = templates[0];
    const normalizedInstructions = additionalInstructions.trim();

    if (!template && !practiceA && !practiceB) {
      toast.error('At least one document is required to merge');
      return;
    }
    if (normalizedInstructions.length > MAX_ADDITIONAL_INSTRUCTIONS_LEN) {
      toast.error(`Additional instructions must be ${MAX_ADDITIONAL_INSTRUCTIONS_LEN} characters or fewer`);
      return;
    }

    if (STREAMING_ENABLED) {
      const params = new URLSearchParams();
      if (template?.id) params.set('template_doc_id', String(template.id));
      if (practiceA?.id) params.set('practice_a_doc_id', String(practiceA.id));
      if (practiceB?.id) params.set('practice_b_doc_id', String(practiceB.id));
      if (normalizedInstructions) params.set('additional_instructions', normalizedInstructions);
      setStreamingEndpoint(`/api/projects/${projectId}/merge/stream?${params.toString()}`);
      setStreamedVersionId(null);
      setIsStreaming(true);
      return;
    }

    try {
      toast.loading('Merging policies...', { id: 'merge' });
      const { data } = await api.post(`/api/projects/${projectId}/merge`, {
        template_doc_id: template?.id || null,
        practice_a_doc_id: practiceA?.id || null,
        practice_b_doc_id: practiceB?.id || null,
        additional_instructions: normalizedInstructions || null,
      });
      toast.success(data.has_ambiguity ? 'Ambiguity detected!' : 'Merge complete!', { id: 'merge' });
      navigate(`/projects/${projectId}/versions/${data.version_id}`);
    } catch {
      toast.error('Merge failed', { id: 'merge' });
    }
  };

  const handleViewDocument = async (doc) => {
    try {
      const { data } = await api.get(`/api/documents/${doc.id}`);
      setViewingDoc(data);
    } catch {
      toast.error('Failed to load document content');
    }
  };

  if (loading) return <div className="loading">Loading project...</div>;

  return (
    <div className="project-page">
      <div className="project-page__header">
        <h1>{project.name}</h1>
        <p>{project.description}</p>
      </div>

      <section className="panel panel--spaced">
        <div className="row-actions">
          <h3>Versions</h3>
          {latestVersion && (
            <button
              className="btn"
              onClick={() => navigate(`/projects/${projectId}/versions/${latestVersion.id}`)}
            >
              Latest Version
            </button>
          )}
        </div>
        {versions.length === 0 && <p className="muted">No merged versions yet.</p>}
        {versions.length > 0 && (
          <div className="versions-inline-list">
            {versions.map((v) => (
              <button
                key={v.id}
                className="btn btn--sm"
                onClick={() => navigate(`/projects/${projectId}/versions/${v.id}`)}
              >
                v{v.version_number} • {new Date(v.created_at).toLocaleDateString()}
              </button>
            ))}
          </div>
        )}
      </section>

      <div className="document-sections">
        <div className="document-card">
          <h3>TEMPLATES</h3>
          {templates.length === 0 && <p className="muted">No templates uploaded</p>}
          {templates.map((doc) => (
            <div key={doc.id} className="doc-info">
              <p>File: {doc.filename || `Template ${doc.id}`}</p>
              <p>Uploaded: {new Date(doc.uploaded_at).toLocaleString()}</p>
              <button className="btn btn--sm doc-view-btn" onClick={() => handleViewDocument(doc)}>
                View Content
              </button>
            </div>
          ))}
          <div className="upload-area">
            <input
              type="file"
              id="upload-template"
              onChange={(e) => handleFileUpload(e, 'template')}
              className="visually-hidden"
            />
            <label htmlFor="upload-template" className="btn">
              {uploading ? 'Uploading...' : 'Upload Template'}
            </label>
          </div>
        </div>

        {['practice_a', 'practice_b'].map((type) => {
          const doc = documents.find((item) => item.document_type === type);
          return (
            <div key={type} className="document-card">
              <h3>{type.replace('_', ' ').toUpperCase()}</h3>
              {doc ? (
                <div className="doc-info">
                  <p>File: {doc.filename}</p>
                  <p>Uploaded: {new Date(doc.uploaded_at).toLocaleString()}</p>
                  <button className="btn btn--sm doc-view-btn" onClick={() => handleViewDocument(doc)}>
                    View Content
                  </button>
                </div>
              ) : (
                <div className="upload-area">
                  <input
                    type="file"
                    id={`upload-${type}`}
                    onChange={(e) => handleFileUpload(e, type)}
                    className="visually-hidden"
                  />
                  <label htmlFor={`upload-${type}`} className="btn">
                    {uploading ? 'Uploading...' : `Upload ${type.replace('_', ' ')}`}
                  </label>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <TemplateComparison
        projectId={projectId}
        documents={documents}
        onMerged={(data) => navigate(`/projects/${projectId}/versions/${data.version_id}`)}
      />

      <section className="panel panel--spaced merge-instructions-panel">
        <h3>Additional Merge Instructions (Optional)</h3>
        <p className="muted">
          Provide merge-specific requirements such as compliance focus, terminology, or areas that need explicit attention.
        </p>
        <textarea
          value={additionalInstructions}
          onChange={(e) => setAdditionalInstructions(e.target.value)}
          onFocus={() => {
            setShowInstructionSuggestions(true);
            fetchInstructionSuggestions(additionalInstructions.trim());
          }}
          onBlur={() => {
            setTimeout(() => setShowInstructionSuggestions(false), 120);
          }}
          placeholder='e.g., "Please ensure that the generated policy is compliant with current best practices and CQC guidance."'
          maxLength={MAX_ADDITIONAL_INSTRUCTIONS_LEN}
        />
        {showInstructionSuggestions && (
          <div className="merge-instructions-suggestions">
            {suggestionsLoading && <p className="muted">Loading suggestions...</p>}
            {!suggestionsLoading && instructionSuggestions.length === 0 && (
              <p className="muted">No previous instruction suggestions yet.</p>
            )}
            {!suggestionsLoading && instructionSuggestions.length > 0 && (
              <ul>
                {instructionSuggestions.map((item, index) => (
                  <li key={`${index}-${item.slice(0, 24)}`}>
                    <button
                      type="button"
                      className="btn btn--sm"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setAdditionalInstructions(item);
                        setShowInstructionSuggestions(false);
                      }}
                    >
                      {item}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        <p className="muted merge-instructions-panel__meta">
          {additionalInstructions.length}/{MAX_ADDITIONAL_INSTRUCTIONS_LEN} characters
        </p>
      </section>

      <div className="project-actions">
        <button className="btn" onClick={() => navigate(`/projects/${projectId}/documents`)}>
          View Documents
        </button>
        {streamedVersionId && !isStreaming && (
          <button
            className="btn btn--sm"
            onClick={() => navigate(`/projects/${projectId}/versions/${streamedVersionId}`)}
          >
            Open Merged Version
          </button>
        )}
        {latestMergeReady ? (
          <button
            className="btn btn--primary btn--large"
            onClick={() => navigate(`/projects/${projectId}/versions/${latestVersion.id}`)}
          >
            Open Latest Merged Policy
          </button>
        ) : (
          <button className="btn btn--primary btn--large" onClick={handleMerge} disabled={uploading || isStreaming}>
            Merge Policies
          </button>
        )}
      </div>

      <StreamingOutput
        enabled={isStreaming}
        endpoint={streamingEndpoint}
        onComplete={(payload) => {
          setIsStreaming(false);
          setStreamedVersionId(payload.version_id || null);
          fetchProjectData();
          toast.success(payload.has_ambiguity ? 'Ambiguity detected!' : 'Merge complete!');
        }}
        onError={() => {
          setIsStreaming(false);
          toast.error('Streaming merge failed');
        }}
      />

      {viewingDoc && (
        <DocumentViewModal
          document={viewingDoc}
          onClose={() => setViewingDoc(null)}
        />
      )}
    </div>
  );
};

export default ProjectPage;
